// src/modules/accounting/pages/GstCentrePage.jsx
//
// The GST Centre. Four tabs:
//   Summary — month-by-month output vs input vs net payable vs paid (GSTR-style)
//   Output  — GST charged on smelter sale invoices (auto)
//   Input   — GST paid on expenses / ITC (auto from Expenses with GST details)
//   Manual  — approval-gated entries: GST payment to govt, liability increase, ITC credit

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Button, Modal, Form, Input, InputNumber, Select, Tag, Space,
  message, Typography, Row, Col, Tooltip, Popconfirm, Alert, Spin,
  Statistic, DatePicker, Radio, Badge, Tabs
} from 'antd';
import { PlusOutlined, ReloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import DataTable from '../../../components/UI/DataTable';
import {
  fetchGstSummary, fetchGstOutput, fetchGstInput, fetchGstManual,
  createGstManualEntry, cancelGstManualEntry,
} from '../api';

const { Title, Text } = Typography;

const MANUAL_TYPE_META = {
  GST_PAYMENT: { label: 'GST Payment to Govt', color: 'green', hint: 'Cash/bank paid against the GST liability. Posts: Dr Taxes Payable / Cr Cash or Bank.' },
  LIABILITY_INCREASE: { label: 'Liability Increase', color: 'volcano', hint: 'RCM output, late fee, interest, notice demand. Posts: Dr Other Expenses / Cr Taxes Payable.' },
  ITC_CREDIT: { label: 'ITC Credit', color: 'cyan', hint: 'Input credit claimed outside expenses. Posts: Dr GST Input Credit (1330) / Cr Taxes Payable.' },
};

const STATUS_BADGE = {
  PENDING: { status: 'processing', text: 'Pending Approval' },
  COMPLETED: { status: 'success', text: 'Completed' },
  REJECTED: { status: 'error', text: 'Rejected' },
  CANCELLED: { status: 'default', text: 'Cancelled' },
};

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');
const monthLabel = (p) => (p ? dayjs(`${p}-01`).format('MMM YYYY') : '—');

const GST_TABS = ['summary', 'output', 'input', 'manual'];

export default function GstCentrePage() {
  // ?tab=manual lets approval deep links land on the approval-gated entries tab.
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return GST_TABS.includes(t) ? t : 'summary';
  });
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 25 });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(null);   // dayjs month or null
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (period) params.period = period.format('YYYY-MM');

      const sumRes = await fetchGstSummary();
      setSummary(sumRes.data);

      if (activeTab === 'output') {
        const res = await fetchGstOutput(params);
        setRows(res.data || []); setMeta(res.meta || {});
      } else if (activeTab === 'input') {
        const res = await fetchGstInput(params);
        setRows(res.data || []); setMeta(res.meta || {});
      } else if (activeTab === 'manual') {
        const res = await fetchGstManual(params);
        setRows(res.data || []); setMeta(res.meta || {});
      }
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to load GST data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, period]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (values) => {
    setSaving(true);
    try {
      const res = await createGstManualEntry({
        type: values.type,
        amount: values.amount,
        paymentMode: values.paymentMode,
        entryDate: values.entryDate ? values.entryDate.toISOString() : undefined,
        narration: values.narration || '',
        referenceNo: values.referenceNo || '',
      });
      messageApi.success(res.message || 'Entry submitted');
      setModalOpen(false);
      form.resetFields();
      setActiveTab('manual');
      load();
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to submit entry');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (row) => {
    setCancellingId(row._id);
    try {
      await cancelGstManualEntry(row._id);
      messageApi.success(`Entry ${row.entryId} cancelled`);
      load();
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setCancellingId(null);
    }
  };

  /* ── Columns per tab ── */

  const summaryColumns = [
    { title: 'Month', dataIndex: 'period', width: 100, render: monthLabel },
    { title: 'Output GST', dataIndex: 'outputGst', align: 'right', render: (v) => `₹${fmt(v)}` },
    { title: '+ Liability Adj.', dataIndex: 'liabilityIncrease', align: 'right', render: (v) => v ? `₹${fmt(v)}` : '—' },
    { title: 'Input GST', dataIndex: 'inputGst', align: 'right', render: (v) => `₹${fmt(v)}` },
    {
      title: 'ITC Eligible', dataIndex: 'itcEligible', align: 'right',
      render: (v, r) => (
        <Tooltip title="Input GST on expenses that have a supplier GSTIN — claimable in GSTR-3B">
          <Text type={v < r.inputGst ? 'warning' : undefined}>₹{fmt(v)}</Text>
        </Tooltip>
      ),
    },
    { title: '+ ITC Credits', dataIndex: 'itcCredit', align: 'right', render: (v) => v ? `₹${fmt(v)}` : '—' },
    {
      title: 'Net Payable', dataIndex: 'netPayable', align: 'right',
      render: (v) => <Text strong>₹{fmt(v)}</Text>,
    },
    { title: 'Paid', dataIndex: 'payments', align: 'right', render: (v) => v ? `₹${fmt(v)}` : '—' },
    {
      title: 'Balance', dataIndex: 'balance', align: 'right',
      render: (v) => <Text strong style={{ color: v > 0 ? '#cf1322' : '#3f8600' }}>₹{fmt(v)}</Text>,
    },
  ];

  const outputColumns = [
    { title: 'Invoice No', dataIndex: 'invoiceNo', width: 140, render: (v) => <Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> },
    { title: 'Date', dataIndex: 'invoiceDate', width: 100, render: (d) => d ? dayjs(d).format('DD/MM/YYYY') : '—' },
    { title: 'Smelter', dataIndex: ['smelterId', 'name'], ellipsis: true },
    { title: 'Taxable Value', dataIndex: 'taxableValue', align: 'right', render: (v) => `₹${fmt(v)}` },
    { title: 'Type', dataIndex: 'gstType', width: 80, render: (t) => <Tag>{t === 'INTER' ? 'IGST' : 'CGST+SGST'}</Tag> },
    { title: 'CGST', dataIndex: 'cgstAmount', align: 'right', render: (v) => v ? `₹${fmt(v)}` : '—' },
    { title: 'SGST', dataIndex: 'sgstAmount', align: 'right', render: (v) => v ? `₹${fmt(v)}` : '—' },
    { title: 'IGST', dataIndex: 'igstAmount', align: 'right', render: (v) => v ? `₹${fmt(v)}` : '—' },
    { title: 'Total GST', dataIndex: 'gstAmount', align: 'right', render: (v) => <Text strong>₹{fmt(v)}</Text> },
    { title: 'Invoice Total', dataIndex: 'total', align: 'right', render: (v) => `₹${fmt(v)}` },
  ];

  const inputColumns = [
    { title: 'Expense ID', dataIndex: 'expenseId', width: 130, render: (v) => <Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> },
    { title: 'Date', dataIndex: 'expenseDate', width: 100, render: (d) => d ? dayjs(d).format('DD/MM/YYYY') : '—' },
    { title: 'Category', dataIndex: 'category', width: 150, render: (v) => <Tag>{(v || '').replace(/_/g, ' ')}</Tag> },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    { title: 'Supplier', dataIndex: ['gst', 'partyName'], width: 140, render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: 'GSTIN', dataIndex: ['gst', 'gstin'], width: 150,
      render: (v) => v
        ? <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text>
        : <Tooltip title="No supplier GSTIN — ITC cannot be claimed for this expense"><Tag color="orange">Missing</Tag></Tooltip>,
    },
    { title: 'Taxable', dataIndex: 'amount', align: 'right', render: (v) => `₹${fmt(v)}` },
    { title: 'GST', dataIndex: 'taxAmount', align: 'right', render: (v) => <Text strong>₹{fmt(v)}</Text> },
    { title: 'Status', dataIndex: 'status', width: 100, render: (s) => <Tag color={s === 'PAID' ? 'green' : 'blue'}>{s}</Tag> },
  ];

  const manualColumns = [
    { title: 'Ref', dataIndex: 'entryId', width: 110, render: (v) => <Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> },
    { title: 'Date', dataIndex: 'entryDate', width: 100, render: (d) => d ? dayjs(d).format('DD/MM/YYYY') : '—' },
    { title: 'Period', dataIndex: 'period', width: 95, render: monthLabel },
    {
      title: 'Type', dataIndex: 'type', width: 170,
      render: (t) => { const m = MANUAL_TYPE_META[t] || { label: t, color: 'default' }; return <Tag color={m.color}>{m.label}</Tag>; },
    },
    { title: 'Amount', dataIndex: 'amount', align: 'right', width: 120, render: (v) => <Text strong>₹{fmt(v)}</Text> },
    { title: 'Mode', dataIndex: 'paymentMode', width: 80, render: (v) => v || '—' },
    { title: 'Reference No', dataIndex: 'referenceNo', width: 130, render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Narration', dataIndex: 'narration', ellipsis: true },
    {
      title: 'Journal', dataIndex: 'journalEntry', width: 110,
      render: (je) => je?.entryId ? <Tag color="blue">{je.entryId}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 140,
      render: (s) => { const b = STATUS_BADGE[s] || { status: 'default', text: s }; return <Badge status={b.status} text={b.text} />; },
    },
    {
      title: 'Actions', key: 'actions', width: 100, fixed: 'right',
      render: (_, row) => row.status === 'PENDING' ? (
        <Popconfirm
          title="Withdraw this pending entry?"
          okText="Withdraw" okButtonProps={{ danger: true }}
          onConfirm={() => handleCancel(row)}
        >
          <Button size="small" danger loading={cancellingId === row._id}>Cancel</Button>
        </Popconfirm>
      ) : <Text type="secondary">—</Text>,
    },
  ];

  if (loading && !summary) {
    return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;
  }

  const totals = summary?.totals || {};
  const listTable = (columns, listKey) => (
    <DataTable persistId={`accounting.gst-centre.${listKey}`} serverPaginated
      dataSource={rows.map((r) => ({ ...r, key: r._id }))}
      columns={columns}
      loading={loading}
      size="small"
      scroll={{ x: 1200 }}
      pagination={{
        current: meta.page || 1, pageSize: meta.limit || 25, total: meta.total || 0,
        showTotal: (t) => `${t} rows`,
        onChange: (p) => setPage(p),
      }}
    />
  );

  return (
    <div>
      {contextHolder}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={5} style={{ margin: 0 }}>GST Centre</Title></Col>
        <Col>
          <Space>
            <DatePicker picker="month" value={period} placeholder="Filter month"
              onChange={(v) => { setPage(1); setPeriod(v); }} allowClear format="MMM YYYY" />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalOpen(true); }}>
              New Manual Entry
            </Button>
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={load} loading={loading} /></Tooltip>
          </Space>
        </Col>
      </Row>

      {summary?.pendingCount > 0 && (
        <Alert
          style={{ marginBottom: 16 }} type="info" showIcon icon={<ClockCircleOutlined />}
          message={`${summary.pendingCount} manual GST entr${summary.pendingCount === 1 ? 'y' : 'ies'} awaiting approval`}
          description="They post to the books once approved (Workflow → My Approvals)."
        />
      )}

      {/* Headline cards (last 12 months) */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Output GST (12 mo)" value={fmt(totals.outputGst)} prefix="₹" valueStyle={{ fontSize: 18, color: '#cf1322' }} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Input GST / ITC (12 mo)" value={fmt(totals.inputGst)} prefix="₹" valueStyle={{ fontSize: 18, color: '#3f8600' }} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Net Payable (12 mo)" value={fmt(totals.netPayable)} prefix="₹" valueStyle={{ fontSize: 18 }} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Outstanding Balance" value={fmt(totals.balance)} prefix="₹" valueStyle={{ fontSize: 18, color: (totals.balance || 0) > 0 ? '#cf1322' : '#3f8600' }} /></Card></Col>
      </Row>

      <Card size="small">
        <Tabs
          activeKey={activeTab}
          onChange={(k) => { setPage(1); setActiveTab(k); }}
          items={[
            {
              key: 'summary', label: 'Monthly Summary',
              children: (
                <DataTable persistId="accounting.gst-centre.summary"
                  dataSource={(summary?.months || []).map((m) => ({ ...m, key: m.period }))}
                  columns={summaryColumns}
                  loading={loading}
                  size="small"
                  pagination={false}
                  scroll={{ x: 1000 }}
                />
              ),
            },
            { key: 'output', label: 'Output GST (Sales)', children: listTable(outputColumns, 'output') },
            { key: 'input', label: 'Input GST (Expenses / ITC)', children: listTable(inputColumns, 'input') },
            { key: 'manual', label: 'Manual Entries', children: listTable(manualColumns, 'manual') },
          ]}
        />
      </Card>

      {/* Manual entry modal */}
      <Modal
        title="New Manual GST Entry"
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="Submit for Approval"
        width={520}
        destroyOnClose
      >
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="Requires approval"
          description="Nothing posts to the books until this entry is approved in Workflow → My Approvals."
        />
        <Form form={form} layout="vertical" onFinish={handleCreate}
          initialValues={{ paymentMode: 'BANK', entryDate: dayjs() }}>
          <Form.Item name="type" label="Entry Type" rules={[{ required: true, message: 'Required' }]}>
            <Select placeholder="What happened?"
              options={Object.entries(MANUAL_TYPE_META).map(([value, m]) => ({ value, label: m.label }))} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.type !== c.type}>
            {({ getFieldValue }) => {
              const m = MANUAL_TYPE_META[getFieldValue('type')];
              return m ? (
                <div style={{ marginTop: -12, marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{m.hint}</Text>
                </div>
              ) : null;
            }}
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true, message: 'Required' }]}>
                <InputNumber min={0.01} precision={2} style={{ width: '100%' }} prefix="₹" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item noStyle shouldUpdate={(p, c) => p.type !== c.type}>
                {({ getFieldValue }) => getFieldValue('type') === 'GST_PAYMENT' && (
                  <Form.Item name="paymentMode" label="Paid From" rules={[{ required: true }]}>
                    <Radio.Group optionType="button"
                      options={[{ label: 'Bank', value: 'BANK' }, { label: 'Cash', value: 'CASH' }]} />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="entryDate" label="Entry Date" tooltip="Drives which GST month this lands in">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="referenceNo" label="Challan / ARN / Notice No.">
                <Input placeholder="Optional reference" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="narration" label="Narration / Reason" rules={[{ required: true, message: 'Explain why — approvers see this' }]}>
            <Input.TextArea rows={2} placeholder="e.g. GSTR-3B payment for Jul 2026" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
